import { kebabCase } from 'lodash';
import { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Button,
  Flex,
  SelectField,
  spaces,
  Tooltip,
  Text,
  type SelectProps,
  type CustomComponents,
} from '@electricjs/arc';
import { LocationOption, SiteOption } from '../helpers/transformOptions';

const TooltipWrapper = styled(Flex)`
  // Needed to display the tooltip on hover
  pointer-events: auto;
  // Display the tooltip when any part of the option is hovered
  width: 100%;
`;

export type SiteSelectionProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  siteOptions: SiteOption[];
  isSitesFetching: boolean;
  required: boolean;
  canUseLocations: boolean;
  locationOptions: LocationOption[];
  isFetchingLocations: boolean;
  onAddSite?: () => void;
  menuPlacement?: SelectProps<SiteOption>['menuPlacement'];
};

const SiteSelection = <T extends FieldValues = FieldValues>({
  control,
  siteOptions,
  isSitesFetching,
  required,
  canUseLocations,
  locationOptions,
  isFetchingLocations,
  onAddSite,
  menuPlacement = 'auto',
}: SiteSelectionProps<T>) => {
  const { t } = useTranslation('assets');

  // Custom Option component that renders disabled options with tooltips when applicable
  const customComponents: CustomComponents | undefined = {
    Option(props: {
      isDisabled?: boolean;
      isSelected?: boolean;
      data?: SiteOption;
      children: React.ReactNode;
    }) {
      const isDisabled = props.isDisabled || props.isSelected;
      const tooltipContent = props.data?.disabledTooltip;

      // Generate test ID based on option value (matching ReactSelect's default behavior)
      const testId = props.data?.label
        ? `option-${kebabCase(props.data.label)}`
        : undefined;

      const content = (
        <Text
          tag="span"
          intent={isDisabled ? 'disabled' : 'primary'}
          width="100%"
          testId={testId}>
          {props.children}
        </Text>
      );

      // Wrap disabled options with tooltip
      if (isDisabled && tooltipContent) {
        return (
          <Tooltip
            content={tooltipContent}
            placement="top-start"
            hideWhenReferenceDisabled={false}
            wrapperWidth="100%">
            <TooltipWrapper>{content}</TooltipWrapper>
          </Tooltip>
        );
      }

      // Regular option without tooltip
      return content;
    },
  };

  return (
    <Flex flexDirection="column" width="100%">
      <Flex width="100%" gap={spaces.space_m}>
        <Flex width={onAddSite ? '75%' : '100%'} flexDirection="column">
          <SelectField
            id="asset-site"
            name="site"
            control={control}
            label={t('newAssetForm.site')}
            options={siteOptions}
            isLoading={isSitesFetching}
            isClearable
            isSearchable
            placeholder={t('sites.selectSite')}
            required={required}
            menuPlacement={menuPlacement}
            customComponents={customComponents}
          />
        </Flex>
        {onAddSite && (
          <Flex width="25%" alignItems="flex-start" paddingTop="2rem">
            <Button
              id="asset-add-site-button"
              variant="outline"
              iconBefore="add-circle"
              onClick={onAddSite}>
              {t('sites.addSite')}
            </Button>
          </Flex>
        )}
      </Flex>
      {canUseLocations && (
        <Flex width="calc(100% - 2rem)">
          <Flex width="75%">
            <SelectField
              id="asset-location"
              control={control}
              name="location"
              label={t('newAssetForm.location')}
              placeholder={t('sites.selectLocation')}
              options={locationOptions}
              isLoading={isFetchingLocations}
              isSearchable
              isClearable
              menuPlacement="auto"
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default SiteSelection;
